import { Navbar, Footer } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Package, CalendarDays, Settings, AlertTriangle, Mail, Phone, MapPin, Building2, Globe, Calendar, User as UserIcon, Hash, Briefcase, Linkedin, Instagram, Facebook, MessageCircle, Megaphone, ExternalLink, Power, Trash2, Activity, Loader2 } from "lucide-react";
import { AlertsManager } from "@/components/admin/AlertsManager";
import { UsersTab } from "@/components/admin/UsersTab";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";

export default function Admin() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<any[]>([]);
  const [allBookings, setAllBookings] = useState<any[]>([]);
  const [allListings, setAllListings] = useState<any[]>([]);
  const [disputes, setDisputes] = useState<any[]>([]);
  const [feeConfig, setFeeConfig] = useState<{ id: string; transaction_fee_pct: number; service_fee_pct: number } | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<any | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedListing, setSelectedListing] = useState<any | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const removeProfileFromListRef = useRef<((id: string) => void) | null>(null);

  const handleDeleteUser = async () => {
    const target = profileToDelete || selectedProfile;
    if (!target) return;
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-delete-user", {
        body: { user_id: target.id },
      });
      if (error) throw error;
      const failures = (data as any)?.failures || [];
      removeProfileFromListRef.current?.(target.id);
      if (failures.length > 0) {
        console.warn("admin-delete-user partial failures", failures);
        toast.warning("Användaren har tagits bort men vissa data kunde inte raderas automatiskt. Se loggen för detaljer.");
      } else {
        toast.success("Användaren har tagits bort permanent.");
      }
      setDeleteConfirmOpen(false);
      setProfileToDelete(null);
      setSelectedProfile(null);
    } catch (e: any) {
      console.error("delete user failed", e);
      toast.error(e?.message || "Kunde inte ta bort användaren");
    } finally {
      setDeleting(false);
    }
  };

  const loadListings = async () => {
    const { data: listings, error } = await supabase
      .from("listings")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Kunde inte hämta annonser");
      return;
    }
    if (!listings) return;
    const sellerIds = Array.from(new Set(listings.map((l: any) => l.seller_id).filter(Boolean)));
    let sellerMap: Record<string, any> = {};
    if (sellerIds.length) {
      const { data: sellers } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, username")
        .in("id", sellerIds);
      if (sellers) sellerMap = Object.fromEntries(sellers.map((s: any) => [s.id, s]));
    }
    setAllListings(listings.map((l: any) => ({ ...l, seller: sellerMap[l.seller_id] || null })));
  };

  useEffect(() => {
    if (!hasRole("admin")) return;

    supabase.from("profiles").select("*").order("created_at", { ascending: false }).then(({ data }) => { if (data) setProfiles(data); });
    supabase.from("bookings").select("*, services(title), profiles:customer_id(full_name), provider_profiles:provider_id(business_name)").order("created_at", { ascending: false }).then(({ data }) => { if (data) setAllBookings(data); });
    loadListings();
    supabase.from("disputes").select("*, profiles:opened_by(full_name)").order("created_at", { ascending: false }).then(({ data }) => { if (data) setDisputes(data); });
    supabase.from("fee_config").select("*").single().then(({ data }) => { if (data) setFeeConfig(data as any); });
  }, []);

  const deactivateListing = async (id: string) => {
    const { error } = await supabase.from("listings").update({ status: "inaktiv" }).eq("id", id);
    if (error) return toast.error("Kunde inte inaktivera annons");
    toast.success("Annons inaktiverad");
    setSelectedListing(null);
    loadListings();
  };

  const deleteListing = async (id: string) => {
    if (!confirm("Radera annonsen permanent?")) return;
    const { error } = await supabase.from("listings").delete().eq("id", id);
    if (error) return toast.error("Kunde inte radera annons");
    toast.success("Annons raderad");
    setSelectedListing(null);
    loadListings();
  };

  const updateFees = async () => {
    if (!feeConfig) return;
    const { error } = await supabase.from("fee_config").update({
      transaction_fee_pct: feeConfig.transaction_fee_pct,
      service_fee_pct: feeConfig.service_fee_pct,
      updated_at: new Date().toISOString(),
    }).eq("id", feeConfig.id);
    if (error) toast.error("Kunde inte uppdatera avgifter");
    else toast.success("Avgifter uppdaterade!");
  };

  const resolveDispute = async (id: string) => {
    const { error } = await supabase.from("disputes").update({ status: "resolved", resolved_at: new Date().toISOString() }).eq("id", id);
    if (error) toast.error("Fel vid lösning av tvist");
    else {
      toast.success("Tvist löst");
      setDisputes(prev => prev.map(d => d.id === id ? { ...d, status: "resolved" } : d));
    }
  };

  const approveCompany = async (id: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .update({ admin_approved: true })
      .eq("id", id)
      .select("id, admin_approved");
    if (error) {
      toast.error("Kunde inte godkänna företag");
      return;
    }
    if (!data || data.length === 0) {
      toast.error("Ingen rad uppdaterades – saknar behörighet?");
      return;
    }
    toast.success("Företag godkänt");
    setProfiles(prev => prev.map(p => p.id === id ? { ...p, admin_approved: true } : p));
    setSelectedProfile(prev => prev && prev.id === id ? { ...prev, admin_approved: true } : prev);
  };

  if (!hasRole("admin")) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <div className="container flex-1 py-20 text-center">
          <p className="text-lg font-medium">Åtkomst nekad</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="container flex-1 py-8">
        <h1 className="mb-6 font-display text-2xl font-bold">Adminpanel</h1>
        
        {/* Stats */}
        <div className="mb-8 grid gap-4 sm:grid-cols-4">
          {[
            { label: "Användare", count: profiles.length, icon: Users },
            { label: "Bokningar", count: allBookings.length, icon: CalendarDays },
            { label: "Annonser", count: allListings.length, icon: Package },
            { label: "Öppna tvister", count: disputes.filter(d => d.status === "open").length, icon: AlertTriangle },
          ].map(s => (
            <div key={s.label} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <s.icon className="h-4 w-4" />
                <span className="text-sm">{s.label}</span>
              </div>
              <p className="mt-1 text-2xl font-bold">{s.count}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="users">
          <TabsList className="mb-6">
            <TabsTrigger value="users"><Users className="mr-2 h-4 w-4" /> Användare</TabsTrigger>
            <TabsTrigger value="bookings"><CalendarDays className="mr-2 h-4 w-4" /> Bokningar</TabsTrigger>
            <TabsTrigger value="listings"><Package className="mr-2 h-4 w-4" /> Annonser</TabsTrigger>
            <TabsTrigger value="disputes"><AlertTriangle className="mr-2 h-4 w-4" /> Tvister</TabsTrigger>
            <TabsTrigger value="alerts"><Megaphone className="mr-2 h-4 w-4" /> Meddelanden</TabsTrigger>
            
            <TabsTrigger value="settings"><Settings className="mr-2 h-4 w-4" /> Avgifter</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <UsersTab
              onSelectProfile={async (p) => {
                setSelectedProfile(p);
                setSelectedRoles([]);
                const { data } = await supabase.from("user_roles").select("role").eq("user_id", p.id);
                if (data) setSelectedRoles(data.map((r: any) => r.role));
              }}
              onApproveCompany={approveCompany}
              registerRemove={(fn) => { removeProfileFromListRef.current = fn; }}
            />
          </TabsContent>

          <TabsContent value="bookings">
            <div className="space-y-2">
              {allBookings.map(b => (
                <div key={b.id} className="flex items-center justify-between rounded-lg border border-border bg-card p-3">
                  <div>
                    <p className="font-medium text-sm">{(b as any).services?.title || "Tjänst"}</p>
                    <p className="text-xs text-muted-foreground">
                      Kund: {(b as any).profiles?.full_name || "-"} • Leverantör: {(b as any).provider_profiles?.business_name || "-"}
                    </p>
                  </div>
                  <Badge variant="secondary">{b.status}</Badge>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="listings">
            <div className="mb-4 flex justify-end">
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => navigate("/admin/ladda-upp-annons")}
              >
                <Package className="mr-2 h-4 w-4" /> Ladda upp annons
              </Button>
            </div>
            {allListings.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Inga annonser.</p>
            ) : (
              <div className="space-y-2">
                {allListings.map(l => (
                  <button
                    key={l.id}
                    onClick={() => setSelectedListing(l)}
                    className="w-full text-left flex items-center justify-between rounded-lg border border-border bg-card p-3 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {l.images?.[0] ? (
                        <img src={l.images[0]} alt={l.title} className="h-10 w-10 rounded object-cover shrink-0" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-muted shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{l.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{l.category} • {l.location} • Säljare: {l.seller?.full_name || "-"}</p>
                      </div>
                    </div>
                    <Badge variant={l.status === "aktiv" ? "default" : "secondary"}>{l.status}</Badge>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="disputes">
            {disputes.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Inga tvister.</p>
            ) : (
              <div className="space-y-2">
                {disputes.map(d => (
                  <div key={d.id} className="rounded-lg border border-border bg-card p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{d.reason}</p>
                        <p className="text-xs text-muted-foreground">Öppnad av: {(d as any).profiles?.full_name || "-"}</p>
                      </div>
                      <Badge variant={d.status === "open" ? "destructive" : "secondary"}>{d.status}</Badge>
                    </div>
                    {d.status === "open" && (
                      <Button size="sm" className="mt-3" onClick={() => resolveDispute(d.id)}>Lös tvist</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="alerts">
            <AlertsManager />
          </TabsContent>


          <TabsContent value="settings">
            {feeConfig && (
              <div className="max-w-sm space-y-4 rounded-lg border border-border bg-card p-6">
                <h2 className="font-sans text-lg font-semibold">Avgiftsinställningar</h2>
                <div>
                  <label className="mb-1 block text-sm font-medium">Transaktionsavgift (%)</label>
                  <Input
                    type="number"
                    value={feeConfig.transaction_fee_pct}
                    onChange={(e) => setFeeConfig(prev => prev ? { ...prev, transaction_fee_pct: parseFloat(e.target.value) } : prev)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Serviceavgift (%)</label>
                  <Input
                    type="number"
                    value={feeConfig.service_fee_pct}
                    onChange={(e) => setFeeConfig(prev => prev ? { ...prev, service_fee_pct: parseFloat(e.target.value) } : prev)}
                  />
                </div>
                <Button onClick={updateFees}>Spara avgifter</Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <Footer />

      <Dialog open={!!selectedProfile} onOpenChange={(o) => !o && setSelectedProfile(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedProfile && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={selectedProfile.avatar_url || undefined} />
                    <AvatarFallback>{(selectedProfile.full_name || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <DialogTitle className="text-xl">{selectedProfile.full_name || "Namnlös"}</DialogTitle>
                    <DialogDescription className="flex flex-wrap items-center gap-1.5 mt-1">
                      <Badge variant="secondary" className="text-[10px]">{selectedProfile.account_type}</Badge>
                      {selectedProfile.email_verified && <Badge className="text-[10px]">E-post verifierad</Badge>}
                      {selectedProfile.account_type === "company" && selectedProfile.admin_approved && (
                        <Badge className="text-[10px]">Företag verifierat</Badge>
                      )}
                      {selectedRoles.map(r => (
                        <Badge key={r} variant="outline" className="text-[10px]">{r}</Badge>
                      ))}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <section>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Kontakt</h3>
                  <div className="space-y-1.5">
                    <InfoRow icon={Mail} label="E-post" value={selectedProfile.email} />
                    <InfoRow icon={Phone} label="Telefon" value={selectedProfile.phone} />
                    <InfoRow icon={MessageCircle} label="WhatsApp" value={selectedProfile.whatsapp_number} />
                    <InfoRow icon={MapPin} label="Plats" value={selectedProfile.location} />
                    <InfoRow icon={MapPin} label="Län / Kommun" value={[selectedProfile.province, selectedProfile.municipality].filter(Boolean).join(" / ")} />
                    <InfoRow icon={Globe} label="Land" value={selectedProfile.country} />
                  </div>
                </section>

                {selectedProfile.account_type === "company" && (
                  <>
                    <Separator />
                    <section>
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Företag</h3>
                      <div className="space-y-1.5">
                        <InfoRow icon={Building2} label="Företagsnamn" value={selectedProfile.company_name} />
                        <InfoRow icon={Hash} label="Org.nummer" value={selectedProfile.organization_number} />
                        <InfoRow icon={Briefcase} label="Bolagsform" value={selectedProfile.business_form} />
                        <InfoRow icon={Mail} label="Företags-e-post" value={selectedProfile.business_email} />
                        <InfoRow icon={Globe} label="Webbplats" value={selectedProfile.website} />
                        <InfoRow icon={Calendar} label="Grundat år" value={selectedProfile.founded_year} />
                        <InfoRow icon={Users} label="Antal anställda" value={selectedProfile.employee_count} />
                        <InfoRow icon={Briefcase} label="Bransch" value={Array.isArray(selectedProfile.industry) ? selectedProfile.industry.join(", ") : selectedProfile.industry} />
                        <InfoRow icon={Package} label="Produktområden" value={Array.isArray(selectedProfile.product_categories) ? selectedProfile.product_categories.join(", ") : selectedProfile.product_categories} />
                      </div>
                    </section>
                  </>
                )}

                {(selectedProfile.linkedin_url || selectedProfile.instagram_url || selectedProfile.facebook_url || selectedProfile.skogsforum_url) && (
                  <>
                    <Separator />
                    <section>
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Sociala länkar</h3>
                      <div className="space-y-1.5">
                        <InfoRow icon={Linkedin} label="LinkedIn" value={selectedProfile.linkedin_url} />
                        <InfoRow icon={Instagram} label="Instagram" value={selectedProfile.instagram_url} />
                        <InfoRow icon={Facebook} label="Facebook" value={selectedProfile.facebook_url} />
                        <InfoRow icon={Globe} label="Skogsforum" value={selectedProfile.skogsforum_url} />
                      </div>
                    </section>
                  </>
                )}

                <Separator />
                <section>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">System</h3>
                  <div className="space-y-1.5">
                    <InfoRow icon={Hash} label="Användar-ID" value={selectedProfile.id} />
                    <InfoRow icon={Calendar} label="Registrerad" value={selectedProfile.created_at ? new Date(selectedProfile.created_at).toLocaleString("sv-SE") : null} />
                    <InfoRow icon={Calendar} label="Senast uppdaterad" value={selectedProfile.updated_at ? new Date(selectedProfile.updated_at).toLocaleString("sv-SE") : null} />
                  </div>
                </section>
                <Separator />
                <section>
                  <h3 className="text-xs font-semibold text-destructive uppercase tracking-wide mb-2">Farlig zon</h3>
                  <Button
                    variant="outline"
                    className="w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => {
                      setProfileToDelete(selectedProfile);
                      setSelectedProfile(null);
                      setDeleteConfirmOpen(true);
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Ta bort användare
                  </Button>
                </section>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={(o) => { if (!deleting) { setDeleteConfirmOpen(o); if (!o) setProfileToDelete(null); } }}>
        <AlertDialogContent className="z-[100]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive font-bold">Ta bort användare permanent?</AlertDialogTitle>
            <AlertDialogDescription>
              Det här går inte att ångra. Användaren, alla tillhörande annonser, bilder, meddelanden, chattar och köpförslag kommer att raderas permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {profileToDelete && (
            <div className="rounded-lg border border-border bg-muted/50 p-3 text-sm">
              <p className="font-semibold">{profileToDelete.full_name || "Namnlös"}</p>
              <p className="text-muted-foreground break-all">{profileToDelete.email || "—"}</p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteUser(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Ta bort permanent
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!selectedListing} onOpenChange={(o) => !o && setSelectedListing(null)}>
        <DialogContent className="max-w-md">
          {selectedListing && (
            <>
              <DialogHeader>
                <DialogTitle>Annonshantering</DialogTitle>
                <DialogDescription>Snabblänkar och åtgärder för annonsen.</DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                <button
                  onClick={() => { const id = selectedListing.id; setSelectedListing(null); navigate(`/annonser/${id}`); }}
                  className="w-full flex items-center gap-3 rounded-lg border border-border bg-card p-3 hover:bg-accent/50 transition-colors text-left"
                >
                  {selectedListing.images?.[0] ? (
                    <img src={selectedListing.images[0]} alt={selectedListing.title} className="h-14 w-14 rounded object-cover shrink-0" />
                  ) : (
                    <div className="h-14 w-14 rounded bg-muted shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">Annons</p>
                    <p className="font-medium text-sm truncate">{selectedListing.title}</p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>

                {selectedListing.seller_id && (
                  <button
                    onClick={() => { const slug = selectedListing.seller?.username || selectedListing.seller_id; setSelectedListing(null); navigate(`/saljare/${slug}`); }}
                    className="w-full flex items-center gap-3 rounded-lg border border-border bg-card p-3 hover:bg-accent/50 transition-colors text-left"
                  >
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarImage src={selectedListing.seller?.avatar_url || undefined} />
                      <AvatarFallback>{(selectedListing.seller?.full_name || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground">Annonsägare</p>
                      <p className="font-medium text-sm truncate">{selectedListing.seller?.full_name || "Okänd användare"}</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                  </button>
                )}

                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => deactivateListing(selectedListing.id)}
                    disabled={selectedListing.status !== "aktiv"}
                  >
                    <Power className="mr-2 h-4 w-4" />
                    Inaktivera annons
                  </Button>
                  <Button variant="destructive" onClick={() => deleteListing(selectedListing.id)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Radera annons
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon: any; label: string; value: any }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-muted-foreground">{label}: </span>
        <span className="break-words">{String(value)}</span>
      </div>
    </div>
  );
}
